import React from "react";
import { Link } from "react-router-dom";
import { Instagram, MapPin, Phone, Mail } from "lucide-react";
import { useLang, translations } from "@/i18n";
import { LINKS } from "@/lib/assets";
import { LEGAL_LINKS, COMPANY } from "@/lib/legalContent";
import { Newsletter } from "@/components/Newsletter";
import { LOCATIONS } from "@/lib/locations";
import { RitualWordmark } from "@/components/home/RitualWordmark";

export const Footer = () => {
  const { t } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="bg-[#2A2320] text-[#FBF9F6] relative overflow-hidden">
      <div className="ritual-grain" />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-20 pb-10 relative">
        <div className="grid md:grid-cols-12 gap-12 mb-14">
          {/* Brand + description */}
          <div className="md:col-span-4">
            <div className="rh-site-footer-signature" data-testid="site-footer-brand"><RitualWordmark variant="lockup" testId="site-footer-wordmark" /></div>
            <p className="font-serif-accent italic text-lg text-[#E3DCD2] mt-3">{t(translations.footer.tagline)}</p>
            <p className="text-sm text-[#E3DCD2]/70 mt-5 max-w-xs leading-relaxed">{t(translations.footer.description)}</p>
            <div className="flex items-center gap-3 mt-6">
              <a href={LINKS.instagramRitual} target="_blank" rel="noopener noreferrer" data-testid="footer-ig-ritual" aria-label="Instagram @_ritualcafe"
                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10 hover:border-white/30 transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={LINKS.instagramRitual} target="_blank" rel="noopener noreferrer" className="text-sm text-[#E3DCD2] hover:text-white transition-colors">@_ritualcafe</a>
            </div>
          </div>

          {/* Quick links */}
          <div className="md:col-span-2">
            <h4 className="text-xs tracking-overline uppercase text-[#B85C38] mb-5">{t(translations.footer.explore)}</h4>
            <ul className="space-y-3 text-sm text-[#E3DCD2]">
              <li><Link to="/carta" className="hover:text-white transition-colors" data-testid="footer-carta">{t(translations.nav.carta)}</Link></li>
              <li><Link to="/tienda" className="hover:text-white transition-colors" data-testid="footer-tienda">{t(translations.nav.tienda)}</Link></li>
              <li><Link to="/reservas" className="hover:text-white transition-colors" data-testid="footer-reservas">{t(translations.nav.reservar)}</Link></li>
              <li><Link to="/naguara" className="hover:text-white transition-colors" data-testid="footer-naguara">{t(translations.nav.naguara)}</Link></li>
              <li><Link to="/tarjeta" className="hover:text-white transition-colors" data-testid="footer-tarjeta">{t({ es: "Consultar tarjeta regalo", en: "Check gift card", va: "Consultar targeta regal" })}</Link></li>
            </ul>
          </div>

          {/* Venues + contact */}
          <div className="md:col-span-3">
            <h4 className="text-xs tracking-overline uppercase text-[#B85C38] mb-5">{t(translations.footer.contactTitle)}</h4>
            <ul className="space-y-3 text-sm text-[#E3DCD2]">
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#9A8F88]" /><span>{COMPANY.address}</span></li>
              <li><a href={`tel:${LINKS.phoneTel}`} data-testid="footer-phone" className="flex items-center gap-2 hover:text-white transition-colors"><Phone className="w-4 h-4 text-[#9A8F88]" /> {LINKS.phone}</a></li>
              <li><a href={`mailto:${LINKS.email}`} data-testid="footer-email" className="flex items-center gap-2 hover:text-white transition-colors"><Mail className="w-4 h-4 text-[#9A8F88]" /> {LINKS.email}</a></li>
            </ul>
            <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-[#E3DCD2]/60">
              {LOCATIONS.map((loc) => (
                <Link key={loc.slug} to={`/locales/${loc.slug}`} data-testid={`footer-local-${loc.slug}`} className="hover:text-white transition-colors">{loc.shortName}</Link>
              ))}
            </div>
          </div>

          {/* Newsletter */}
          <div className="md:col-span-3">
            <h4 className="text-xs tracking-overline uppercase text-[#B85C38] mb-5">{t(translations.footer.newsletterTitle)}</h4>
            <p className="text-sm text-[#E3DCD2]/80 mb-4 leading-relaxed">{t(translations.footer.newsletterSub)}</p>
            <Newsletter variant="footer" source="footer" />
          </div>
        </div>

        {/* Legal links */}
        <div className="border-t border-white/10 pt-8 mb-6">
          <ul className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#E3DCD2]/70">
            {LEGAL_LINKS.map((l) => (
              <li key={l.slug}>
                <Link to={`/legal/${l.slug}`} data-testid={`footer-legal-${l.slug}`} className="hover:text-white transition-colors">{t(l.label)}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-[#E3DCD2]/50">
          <span>© {year} {COMPANY.name}. {t(translations.footer.rights)}</span>
          <span>CIF {COMPANY.cif}</span>
        </div>
      </div>
    </footer>
  );
};
